/* eslint-disable css-modules/no-unused-class, no-extra-boolean-cast, no-unneeded-ternary */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { translate } from 'translations';
import { coreService } from 'network-utils';
import { FullPageLoader } from 'learning-panel-components';
import { selectCustomerDisplayName, selectToken, selectUserUuid } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { useGetUserSkillTree, getUserSkillTree } from '@queries/core/skillTree';
import { useGetAccountData } from '@queries/platform/accountSettings';
import { LearningPathNodeProps, LearningPathNodeType } from '@components/learningPath/learningPathInterfaces';
import SkillTreeNavigation from './skillTreeNavigation';

const SkillTreeNavigationContainer = () => {
    const [skillTreeData, setSkillTreeData] = useState<any>(null);
    const [learningPathData, setLearningPathData] = useState<any>(null);
    const [learningPathUuid, setLearningPathUuid] = useState<string | null>(null);
    const [overallData, setOverallData] = useState<any>(null);
    const [nickname, setNickname] = useState<string>('');
    const [profilePicture, setProfilePicture] = useState<string>('');
    const [startCalled, setStartCalled] = useState<boolean>(false);
    const [isSelectingNode, setIsSelectingNode] = useState<boolean>(false);

    const navigate = useNavigate();
    const skillTreeUuid = new URLSearchParams(window.location.search).get('uuid');
    const userUuid = useSelector(selectUserUuid);
    const customerDisplayName = useSelector(selectCustomerDisplayName);
    if (!skillTreeUuid) navigate('/app/not-found');
    const learningPathUuidFromUrl = new URLSearchParams(window.location.search).get('selectedLearningPathUuid');
    if (learningPathUuidFromUrl !== learningPathUuid) setLearningPathUuid(learningPathUuidFromUrl);
    const token = useSelector(selectToken);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const generalErrorText = translate('generalError');

    const showError = () => {
        dispatch(
            showAlert({
                value: generalErrorText,
                status: 'error'
            })
        );
    };

    const { data: userSkillTree } = useGetUserSkillTree(skillTreeUuid, learningPathUuid, token);
    const { data: accountData } = useGetAccountData(userUuid);

    useEffect(() => {
        if (!accountData || !accountData.account_data) return;

        setNickname(accountData.account_data.nickname);
        setProfilePicture(accountData.account_data.profile_picture_url);
    }, [accountData]);

    useEffect(() => {
        setLearningPathUuid(learningPathUuid);
        setStartCalled(false);
    }, [learningPathUuidFromUrl]);

    const performStartPath = async (input: {
        uuid: string;
        uuidFromUrl: string;
        skillTreeUuid: string;
    }): Promise<{ success: boolean; error?: string }> => {
        const startPathResponse = await coreService.learningPathApi.start(token, input.uuid);
        return startPathResponse;
    };

    const { mutate: mutateStartPath } = useMutation(performStartPath, {
        onSuccess: response => {
            if (!response?.success) {
                showError();
            }
        },
        onError: () => {
            showError();
        }
    });

    const handleLearningPathData = (userLearningPath: any) => {
        setLearningPathData({
            uuid: userLearningPath.uuid,
            name: userLearningPath.name,
            description: userLearningPath.description,
            level: userLearningPath.level,
            durationMinutes: userLearningPath.duration,
            potentialScore: userLearningPath.potentialScore,
            status: userLearningPath.status,
            completedAt: userLearningPath.completedAt,
            lastSelectedNodeId: userLearningPath.lastSelectedNodeId,
            score: userLearningPath.score,
            languages: userLearningPath.languages,
            nodes: userLearningPath.nodes.map((node: any) => {
                const itemTypesCount = {
                    [coreService.LearningPathContentType.LESSON]: 0,
                    [coreService.LearningPathContentType.CHALLENGE]: 0,
                    [coreService.LearningPathContentType.COURSE]: 0,
                    [coreService.LearningPathContentType.SYSTEM_ASSESSMENT]: 0
                };

                const typeSet = new Set();
                node.items.forEach((item: coreService.LearningPathNodeItem) => {
                    itemTypesCount[item.type]++;
                    typeSet.add(item.type);
                });

                let type;
                if (typeSet.size === 1 && node.items.length === 1) {
                    type = typeSet.values().next().value;
                } else {
                    type = LearningPathNodeType.MIXED;
                }

                const firstUncompleted = node.items.find(
                    (item: any) => item.status !== coreService.ProgressStatus.COMPLETED
                );

                return {
                    id: node.id,
                    uuid: node.uuid,
                    name: node.name,
                    durationMinutes: node.durationMinutes,
                    potentialScore: node.potentialScore,
                    score: node.score,
                    badge: node.badge,
                    status: node.status,
                    children: node.children,
                    progressPercentage: node.progressPercentage,
                    parents: node.parents,
                    nextUrl: firstUncompleted?.url || '',
                    type,
                    itemTypesCount,
                    numberOfItems: node.items.length,
                    isMandatory: node.isMandatory === false ? false : true,
                    isRoot: node.parents.length === 0
                };
            })
        });
    };

    useEffect(() => {
        if (!userSkillTree) return;

        if (userSkillTree.success === false) {
            showError();
            navigate('/app/not-found');
        }
        setLearningPathUuid(userSkillTree.selectedLearningPath.uuid);

        setSkillTreeData({
            uuid: userSkillTree.uuid,
            name: userSkillTree.name,
            completedAt: userSkillTree.completedAt,
            languages: userSkillTree.languages,
            learningPaths: userSkillTree.learningPaths,
            status: userSkillTree.status
        });

        setOverallData({
            score: userSkillTree.points,
            languages: userSkillTree.languages,
            progress: userSkillTree.progress,
            duration: userSkillTree.duration
        });
        handleLearningPathData(userSkillTree.selectedLearningPath);

        if (userSkillTree.selectedLearningPath.status === null && !startCalled) {
            setStartCalled(true);
            mutateStartPath({
                skillTreeUuid: userSkillTree.uuid,
                uuid: userSkillTree.selectedLearningPath.uuid,
                uuidFromUrl: learningPathUuidFromUrl as string
            });
        }
    }, [mutateStartPath, startCalled, userSkillTree, learningPathUuid]);

    const performSelectNode = async (input: {
        node: LearningPathNodeProps;
        learningPathUuid: string;
    }): Promise<{ success: boolean; error?: string }> => {
        const selectNodeResponse = await coreService.learningPathApi.selectNode(token, input.node.uuid);
        return selectNodeResponse;
    };

    const getSelectedLearningPathAfterSelectNode = (oldData: any, node: any) => {
        const selectedLearningPathAfterSelectNode = {
            nodes: oldData?.nodes.map((oldNode: any) => {
                if (oldNode.uuid === node.uuid) {
                    return { ...oldNode, status: coreService.ProgressStatus.COMPLETED };
                }
                return oldNode;
            })
        };
        return selectedLearningPathAfterSelectNode;
    };

    const { mutate: mutateSelectNode } = useMutation(performSelectNode, {
        onSuccess: (response, variables) => {
            setIsSelectingNode(false);
            if (!response?.success) {
                showError();
                return;
            }

            const { node, learningPathUuid } = variables;

            if (node.nextUrl !== '') {
                window.location.href = node.nextUrl;
                return;
            }

            // node content was completed in advance, simply mark it as completed
            queryClient.setQueryData([getUserSkillTree, learningPathUuid, token], (oldData: any) => ({
                ...oldData,
                selectedLearningPath: getSelectedLearningPathAfterSelectNode(oldData, node)
            }));

            queryClient.invalidateQueries([getUserSkillTree, learningPathUuid, token]);
        },
        onError: () => {
            setIsSelectingNode(false);
            showError();
        }
    });

    const onSelectNode = (node: LearningPathNodeProps) => {
        if (isSelectingNode) return;

        setIsSelectingNode(true);
        mutateSelectNode({ node, learningPathUuid: learningPathData.uuid });
    };

    return skillTreeData ? (
        <SkillTreeNavigation
            name={skillTreeData?.name}
            uuid={skillTreeData?.uuid}
            completedAt={skillTreeData?.completedAt}
            learningPaths={skillTreeData?.learningPaths}
            overallData={overallData}
            onSelectLearningPath={setLearningPathUuid}
            status={skillTreeData?.status}
            learningPathData={learningPathData}
            onSelectNode={onSelectNode}
            nickname={nickname}
            profilePicture={profilePicture}
            selectedLearningPathUuid={learningPathUuid}
            tenant={customerDisplayName}
        />
    ) : (
        <FullPageLoader />
    );
};

export default SkillTreeNavigationContainer;
